var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Actions, BasicBanner, BasicIcon, DismissIcon, ErrorBanner, ErrorIcon, IconContainer, InfoIcon, InformationBanner, Message, PendingBanner, PendingIcon, SuccessBanner, SuccessIcon, WarningBanner, WarningIcon, } from './Banner.styled';
import { getAutomation, getDebug, getStyleNames, useCitrusDebug, useMobile } from '@citrus/runtime';
import { useLocalization, useTheme } from '@citrus/user';
import { dismissButton as dismissButtonStyles, spacingBottom, spacingLeft, spacingRight, spacingTop, } from '../styles/citron';
import { Icon } from '@citrus/icon';
import { NotificationButton, BorderlessNotificationButton, BorderlessButton } from '@citrus/button';
import { get, isNotNilOrEmpty, isString } from '@pcty/utils';
import { cx, css as emotionCss } from 'emotion';
import { getChildByType, removeChildrenByType } from 'react-nanny';
import localizationMap from '../localization';
var VERSION = '4.4.0';
var Banner = React.forwardRef(function (_a, ref) {
    var _b;
    var _c, _d, _e, _f;
    var automationId = _a.automationId, children = _a.children, className = _a.className, css = _a.css, hidden = _a.hidden, id = _a.id, onDismiss = _a.onDismiss, renderActionButton = _a.renderActionButton, _g = _a.spacing, spacing = _g === void 0 ? ['top', 'right', 'bottom', 'left'] : _g, _h = _a.type, type = _h === void 0 ? 'basic' : _h, visible = _a.visible, _j = _a.__TYPE, __TYPE = _j === void 0 ? 'Banner' : _j, other = __rest(_a, ["automationId", "children", "className", "css", "hidden", "id", "onDismiss", "renderActionButton", "spacing", "type", "visible", "__TYPE"]);
    var mobile = useMobile(700);
    var lang = useLocalization(localizationMap);
    var citrus2022 = useTheme().citrus2022;
    var isCitron = citrus2022 !== null && citrus2022 !== void 0 ? citrus2022 : false;
    var debug = useCitrusDebug();
    if (visible === false || (hidden && !visible))
        return null;
    var Component = getBanner(type);
    var emotionObj = (_b = {},
        _b[emotionCss(spacingTop)] = isCitron && spacing !== 'none' && spacing.includes('top'),
        _b[emotionCss(spacingRight)] = isCitron && spacing !== 'none' && spacing.includes('right'),
        _b[emotionCss(spacingBottom)] = isCitron && spacing !== 'none' && spacing.includes('bottom'),
        _b[emotionCss(spacingLeft)] = isCitron && spacing !== 'none' && spacing.includes('left'),
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b);
    var classes = cx(emotionObj, className || '');
    var styleNames = {
        spacingTop: (_c = emotionObj[emotionCss(spacingTop)]) !== null && _c !== void 0 ? _c : false,
        spacingRight: (_d = emotionObj[emotionCss(spacingRight)]) !== null && _d !== void 0 ? _d : false,
        spacingBottom: (_e = emotionObj[emotionCss(spacingBottom)]) !== null && _e !== void 0 ? _e : false,
        spacingLeft: (_f = emotionObj[emotionCss(spacingLeft)]) !== null && _f !== void 0 ? _f : false,
    };
    var message = removeChildrenByType(children, ['Icon', 'PctyIcon']);
    return (React.createElement(Component, __assign({ ref: ref, role: getAriaRole(type), styleNames: getStyleNames(styleNames) }, other, getAutomation(automationId), getDebug(debug, __TYPE, VERSION), { className: classes, id: id }),
        React.createElement(IconContainer, { key: "pcty-banner-icon-container-".concat(id) }, getIcon(mobile, type, children, lang, isCitron)),
        React.createElement(Message, __assign({ key: "pcty-banner-message-".concat(id) }, getAutomation(automationId, 'message')), message && isString(message[0]) ? React.createElement("p", null, message[0]) : message),
        React.createElement(Actions, { key: "pcty-banner-actions-".concat(id) },
            renderActionButton && renderActionButtonProxy(type, renderActionButton, isCitron),
            onDismiss && renderDismiss(automationId, mobile, onDismiss, id, type, isCitron))));
});
var getAriaRole = function (type) {
    if (type === 'basic' || type === 'information') {
        return 'status';
    }
    return 'alert';
};
var getBanner = function (type) {
    switch (type) {
        case 'basic':
        default:
            return BasicBanner;
        case 'information':
            return InformationBanner;
        case 'success':
            return SuccessBanner;
        case 'warning':
            return WarningBanner;
        case 'error':
            return ErrorBanner;
        case 'pending':
            return PendingBanner;
    }
};
var getIcon = function (mobile, type, children, lang, isCitron) {
    var custom = getChildByType(children, ['Icon', 'PctyIcon']);
    if (isNotNilOrEmpty(custom))
        return custom;
    switch (type) {
        case 'basic':
        default:
            return mobile && !isCitron ? (React.createElement(InfoIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info })) : (React.createElement(BasicIcon, { "aria-label": "".concat(lang.info, ":"), title: "info" }));
        case 'information':
            return React.createElement(InfoIcon, { "aria-label": "".concat(lang.info, ":"), title: lang.info });
        case 'success':
            return React.createElement(SuccessIcon, { "aria-label": "".concat(lang.success, ":"), title: lang.success });
        case 'warning':
            return React.createElement(WarningIcon, { "aria-label": "".concat(lang.warning, ":"), title: lang.warning });
        case 'error':
            return React.createElement(ErrorIcon, { "aria-label": "".concat(lang.error, ":"), title: lang.error });
        case 'pending':
            return React.createElement(PendingIcon, { "aria-label": "".concat(lang.pending, ":"), title: lang.pending });
    }
};
var renderActionButtonProxy = function (type, renderActionButton, isCitron) {
    if (!renderActionButton)
        return undefined;
    var rendered = renderActionButton();
    var __TYPE = get(rendered, 'props.__TYPE');
    if (__TYPE !== 'NotificationButton')
        return undefined;
    var color = type === 'basic' || (type === 'pending' && !isCitron) ? 'black' : 'white';
    return React.cloneElement(rendered, __assign(__assign({}, rendered.props), { color: color, size: isCitron ? 'medium' : 'small' }));
};
var renderDismiss = function (automationId, mobile, onDismiss, id, type, isCitron) {
    if (!onDismiss)
        return null;
    if (isCitron) {
        return (React.createElement(BorderlessButton, __assign({ className: emotionCss(dismissButtonStyles), onClick: function () { return onDismiss(id); } }, getAutomation(id, 'dismiss')),
            React.createElement(Icon, { bold: true, "data-style": "dismissIcon", name: "delete" })));
    }
    if (mobile) {
        return (React.createElement(BorderlessNotificationButton, __assign({ color: type === 'basic' || type === 'information' || type === 'pending' ? 'black' : 'white', key: "".concat(id, "-dismiss-button"), onClick: function () { return onDismiss(id); }, size: "small" }, getAutomation(automationId, 'dismiss')),
            React.createElement(DismissIcon, null)));
    }
    return (React.createElement(NotificationButton, __assign({ color: type === 'basic' || type === 'pending' ? 'black' : 'white', key: "".concat(id, "-dismiss-button"), onClick: function () { return onDismiss(id); }, size: "small" }, getAutomation(automationId, 'dismiss')), "Dismiss"));
};
Banner.defaultProps = {
    __TYPE: 'Banner',
};
export default Banner;
